import download from 'downloadjs';
import { nanoid } from 'nanoid';
import { omit } from 'ramda';
import {
  put,
  takeEvery,
  takeLatest,
  select,
  call,
  fork,
  take,
  race,
} from 'redux-saga/effects';

import { createStandaloneToast, theme } from '@frontend/legacy-components';

import { ORDER_STATUS } from 'constants/all';

import emailService from 'services/emailService';
import orderService from 'services/orderService';
import supplierSettingsService from 'services/supplierSettingsService';

import history from 'utils/browserHistory';
import { getErrorMessage } from 'utils/stringUtils';

import * as SESSION from '../session';
import * as actions from './actions';
import * as selectors from './selectors';
import * as types from './types';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeEvery(types.UPDATE_PO_REQUEST, watchUpdatePO);
  yield takeLatest(types.ORDER_COPY_REQUEST, watchCopyOrderQuote);
  yield takeEvery(types.ORDER_UPDATE_REQUEST, watchOrderUpdate);
  yield takeLatest(types.CREATE_SHIPMENT_FLOW, watchCreateShipmentFlow);
  yield takeLatest(types.DOWNLOAD_FILES_REQUEST, watchDownloadFiles);
  yield takeLatest(types.CANCEL_ORDER_REQUEST, watchCancelOrder);
  yield takeLatest(types.EXPORT_INVOICE_CSV_REQUEST, watchExportInvoiceCsv);
  yield fork(watchRefundNotificationFlow);
}

function* watchUpdatePO(action) {
  try {
    const groupId = yield select(SESSION.selectors.getGroupId);
    const order = yield select(selectors.getOrder);
    const { files } = action.payload;

    const formData = new FormData();
    if (files) {
      const POFile = files[0];
      formData.append('po_file', POFile);
    }

    yield call(orderService.updatePO, groupId, order.number, formData);
    const nextOrder = yield call(orderService.get, groupId, order.id);

    yield put(actions.updatePO.success(nextOrder));
  } catch (error) {
    yield put(actions.updatePO.error(error));
  }
}

function* watchCopyOrderQuote(action) {
  try {
    const groupId = yield select(SESSION.selectors.getGroupId);
    const { orderNumber } = action.payload;
    const response = yield call(orderService.copy, groupId, orderNumber);
    yield put(actions.copyOrderQuote.success());
    yield call(toast, {
      status: 'success',
      title: 'Copied new quote to draft',
    });
    const { quoteNumber: newQuoteNumber } = response;
    if (newQuoteNumber) {
      history.push(`/quotes/edit/${newQuoteNumber}`);
    }
  } catch (error) {
    yield put(actions.copyOrderQuote.error({ error }));
  }
}

// function* watchPrivateNotesUpdate(action) {
//   try {
//     const { value } = action.payload;
//     const groupId = yield select(SESSION.selectors.getGroupId);
//     const order = yield select(selectors.getOrder);
//     yield call(orderService.updatePrivateNotes, groupId, order.number, value);
//     yield put(actions.updatePrivateNotes.success());
//   } catch (error) {
//     yield put(actions.updatePrivateNotes.error({ error }));
//   }
// }

function* watchOrderUpdate(action) {
  try {
    const { orderId, data } = action.payload;
    const response = yield call(updateOrderSaga, orderId, data);
    yield put(actions.updateOrder.success({ order: response }));
  } catch (error) {
    yield put(actions.updateOrder.error({ error }));
  }
}

function* updateOrderSaga(orderId, data) {
  const groupId = yield select(SESSION.selectors.getGroupId);
  return yield call(orderService.update, groupId, orderId, data);
}

function* createShipment() {
  for (;;) {
    try {
      yield take(types.CREATE_SHIPMENT_REQUEST);
      const order = yield select(selectors.getOrder);
      const groupId = yield select(SESSION.selectors.getGroupId);
      const state = yield select(selectors.getCreateShipmentState);
      const { orderItemToQuantityMapping } = state.data;
      const payload = {
        ...omit(['orderItemToQuantityMapping'], state.data),
        shipmentItems: Object.keys(orderItemToQuantityMapping).map((id) => ({
          orderItem: id,
          quantity: orderItemToQuantityMapping[id],
        })),
      };

      const shipment = yield call(
        orderService.createShipment,
        groupId,
        order.number,
        payload,
      );
      const newOrder = yield call(orderService.get, groupId, order.id);
      yield put(actions.createShipment.success(newOrder));

      return shipment;
    } catch (error) {
      yield put(actions.createShipment.error(error));
    }
  }
}

function* sendInvoice(shipmentId) {
  yield put(actions.createShipment.proceedToSendInvoiceRequest());

  for (;;) {
    try {
      const emailNotificationSettings = yield call(
        supplierSettingsService.getEmailNotificationSettings,
      );
      const templates = yield call(
        emailService.listEmailTemplatesForShipmentInvoices,
        shipmentId,
      );
      yield put(
        actions.createShipment.proceedToSendInvoiceSuccess({
          emailTemplates: templates,
          emailNotificationSettings,
        }),
      );
      break;
    } catch (error) {
      yield put(actions.createShipment.proceedToSendInvoiceError(error));
      // user might try fetching data again
      yield take(types.CREATE_SHIPMENT_PROCEED_TO_SEND_INVOICE_REQUEST);
    }
  }

  for (;;) {
    try {
      const action = yield take(types.CREATE_SHIPMENT_SEND_INVOICE_REQUEST);
      const { data } = action.payload;
      yield call(emailService.sendShipmentInvoice, shipmentId, data);
      yield put(actions.createShipment.sendInvoiceSuccess());
      break;
    } catch (error) {
      yield put(actions.createShipment.sendInvoiceError(error));
    }
  }
}

function* watchCreateShipmentFlow() {
  const shipment = yield call(createShipment);
  yield race({
    sent: call(sendInvoice, shipment.id),
    closed: take(types.CREATE_SHIPMENT_CLOSE),
  });
  yield call(toast, {
    status: 'success',
    title: 'New shipment was successfully created.',
  });
}

export function* watchDownloadFiles(action) {
  const waitId = nanoid();
  try {
    const { orderNumber } = action.payload;
    yield call(toast, {
      status: 'info',
      id: waitId,
      title: 'Please wait...',
    });
    const groupId = yield select(SESSION.selectors.getGroupId);
    const response = yield call(
      orderService.downloadFiles,
      orderNumber,
      groupId,
    );
    yield call(toast.close, waitId);
    switch (response.status) {
      case 'ready': {
        yield call(toast, {
          status: 'info',
          title: 'Download started.',
          duration: 2000,
        });
        yield call(download, response.downloadUrl);
        break;
      }
      case 'uploading': {
        yield call(toast, {
          status: 'info',
          title: response.message,
          isClosable: false,
          duration: null,
        });
        break;
      }
      case 'missing': {
        yield call(toast, {
          status: 'error',
          title: "Can't download files from order",
          description: response.message,
          duration: null,
        });
        break;
      }
    }
    yield put(actions.downloadFiles.success());
  } catch (error) {
    yield put(actions.downloadFiles.error(error));
    yield call(toast.close, waitId);
    yield call(toast, {
      title: "Can't download files from order",
      description: getErrorMessage(error),
      duration: null,
    });
  }
}

function* watchCancelOrder(action) {
  try {
    const { orderId } = action.payload;
    const response = yield call(updateOrderSaga, orderId, {
      status: ORDER_STATUS.CANCELLED,
    });
    yield put(actions.updateOrder.success({ order: response }));
    yield put(actions.cancelOrder.success());
    if (!response.purchaseOrderNumber) {
      yield put(actions.refundNotification.flow(response.number));
    }
  } catch (error) {
    yield put(actions.updateOrder.error({ error }));
    yield put(actions.cancelOrder.error());
  }
}

function* watchRefundNotificationFlow() {
  while (true) {
    const action = yield take(types.REFUND_NOTIFICATION_FLOW);

    try {
      const { orderNumber } = action.payload;
      const groupId = yield select(SESSION.selectors.getGroupId);

      let templates;
      try {
        templates = yield call(
          emailService.listRefundEmailTemplates,
          groupId,
          orderNumber,
        );
      } catch (error) {
        templates = [];
        console.error(error);
      }

      yield put(actions.refundNotification.openModal(templates));
      const requestAction = yield take(types.REFUND_NOTIFICATION_REQUEST);
      const successMessage = yield call(
        emailService.sendRefundEmail,
        groupId,
        orderNumber,
        requestAction.payload,
      );
      yield put(actions.refundNotification.success());
      yield call(toast, {
        status: 'success',
        title: 'Success',
        description: successMessage,
      });
    } catch (error) {
      yield put(actions.refundNotification.error(error));
    }
  }
}

function* watchExportInvoiceCsv() {
  const waitId = nanoid();
  try {
    yield call(toast, {
      id: waitId,
      status: 'info',
      title: 'Downloading invoice CSV file.',
    });
    const groupId = yield select(SESSION.selectors.getGroupId);
    const response = yield call(orderService.downloadInvoiceCSV, groupId);
    if (response) {
      yield call(toast.close, waitId);
      yield call(toast, {
        status: 'info',
        title: response.message,
        duration: null,
      });
    }
    yield put(actions.exportInvoiceCsv.success());
  } catch (error) {
    yield put(actions.exportInvoiceCsv.error(error));
    yield call(toast, {
      status: 'error',
      title: "Couldn't download quicbook invoices.",
      description: getErrorMessage(error),
    });
  }
}
